import { useMemo, useState, useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { LIST_PAGE_SHELL } from "@/components/layout/pageShell";
import type { SalesInvoice } from "@/lib/erp";
import { DocTypeListPageShell } from "@/features/common/components/DocTypeListPageShell";
import { useSalesInvoiceListWithPagination } from "./api/salesInvoiceHooks";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { StatusBadge } from "@/components/ui/status-badge";
import { formatCurrencyTHB } from "@/lib/format/currency";
import { buildNewRoute } from "@/lib/config/docRoutes";
import { injectActionsColumn } from "@/features/list/actionsColumn";
import { GenericInlineDetailPanel } from "@/features/list/GenericInlineDetailPanel";
import { GenericDetailPanel } from "@/features/list/GenericDetailPanel";
import { useDetailPanelState } from "@/features/list/useDetailPanelState";
import { FileText } from "lucide-react";
import type { SubRowColumnInfo } from "@/features/common/components/ListTableBody";
// Note: Using meta-driven list settings, so static configs are no longer needed
// Keeping SALES_INVOICE_LIST_FIELDS for backend fetching, but not using field config

function createSalesInvoiceColumns(opts?: { 
  visibleFieldOrder?: string[]; 
  availableFields?: Array<{ fieldname: string; label: string; fieldtype?: string }>;
}): ColumnDef<SalesInvoice>[] {
  const baseColumns: ColumnDef<SalesInvoice>[] = [
    {
      id: "name",
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Invoice #" />
      ),
      cell: ({ row }) => {
        const invoiceNumber = row.getValue("name") as string;
        return <div className="font-medium">{invoiceNumber}</div>;
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      id: "customer",
      accessorKey: "customer",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Customer" />
      ),
      cell: ({ row }) => {
        const customer = row.getValue("customer") as string;
        return <div>{customer}</div>;
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      id: "posting_date",
      accessorKey: "posting_date",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Posting Date" />
      ),
      cell: ({ row }) => {
        const date = row.getValue("posting_date") as string;
        return (
          <div>{date ? new Date(date).toLocaleDateString() : "-"}</div>
        );
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      id: "status",
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Status" />
      ),
      cell: ({ row }) => {
        const status = row.getValue("status") as string;
        return <StatusBadge>{status}</StatusBadge>;
      },
      enableColumnFilter: true,
      enableSorting: true,
    },
    {
      id: "grand_total",
      accessorKey: "grand_total",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Grand Total" />
      ),
      cell: ({ row }) => {
        const total = row.getValue("grand_total") as number;
        return <div>{formatCurrencyTHB(total)}</div>;
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      id: "outstanding_amount",
      accessorKey: "outstanding_amount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Outstanding" />
      ),
      cell: ({ row }) => {
        const outstanding = row.getValue("outstanding_amount") as number;
        return <div>{formatCurrencyTHB(outstanding)}</div>;
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      id: "company",
      accessorKey: "company",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Company" />
      ),
      cell: ({ row }) => {
        const company = row.getValue("company") as string;
        return <div>{company}</div>;
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
  ];

  // Apply column visibility and ordering based on visibleFieldOrder
  const order = opts?.visibleFieldOrder;
  if (!order || !order.length) return baseColumns;

  // Create map of fieldname -> column
  const byId = new Map(
    baseColumns.map((col) => {
      const id = (col.id as string) || ((col as any).accessorKey as string);
      return [id, col];
    })
  );

  // Order visible columns according to visibleFieldOrder
  const ordered = order
    .map((fieldname) => byId.get(fieldname))
    .filter((col): col is ColumnDef<SalesInvoice> => !!col);

  // Create generic columns for fields in visibleFieldOrder that don't have bespoke columns
  const availableFieldsMap = new Map(
    (opts?.availableFields || []).map((f) => [f.fieldname, f])
  );

  const genericColumns: ColumnDef<SalesInvoice>[] = order
    .filter((fieldname) => !byId.has(fieldname))
    .map((fieldname) => {
      const meta = availableFieldsMap.get(fieldname);
      const label = meta?.label || fieldname;
      const fieldtype = meta?.fieldtype || "Data";

      return {
        id: fieldname,
        accessorKey: fieldname,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={label} />
        ),
        cell: ({ row }) => {
          const value = row.getValue(fieldname);
          
          // Generic rendering based on fieldtype
          if (value === null || value === undefined || value === "") {
            return <span className="text-muted-foreground">-</span>;
          }

          // Check field
          if (fieldtype === "Check") {
            const checked = value === 1 || value === true;
            return <StatusBadge color={checked ? "green" : "gray"}>{checked ? "Yes" : "No"}</StatusBadge>;
          }

          // Date/Datetime fields
          if (fieldtype === "Date" || fieldtype === "Datetime") {
            try {
              const date = new Date(value as string);
              return <div>{date.toLocaleDateString()}</div>;
            } catch {
              return <div>{String(value)}</div>;
            }
          }

          // Currency/Float/Int fields
          if (fieldtype === "Currency" || fieldtype === "Float" || fieldtype === "Int") {
            const num = Number(value);
            if (isNaN(num)) return <div>{String(value)}</div>;
            if (fieldtype === "Currency") {
              return <div>{formatCurrencyTHB(num)}</div>;
            }
            return <div>{num.toLocaleString()}</div>;
          }

          // Link/Select/Data/Text - plain text
          return <div className="truncate max-w-[200px]">{String(value)}</div>;
        },
        enableColumnFilter: false, // v1: keep it simple
        enableSorting: true,
      } as ColumnDef<SalesInvoice>;
    });

  // Keep columns that don't map to a field (e.g. actions) at the end
  // Only keep non-meta columns - meta-backed fields should only appear if in visibleFieldOrder
  const availableFieldnames = new Set(
    (opts?.availableFields || []).map((f) => f.fieldname)
  );

  const extras = baseColumns.filter((col) => {
    const id = (col.id as string) || ((col as any).accessorKey as string);
    
    // Don't keep meta-backed fields - they should only appear if in visibleFieldOrder
    const isMetaField = availableFieldnames.has(id);
    if (isMetaField) return false;
    
    // Don't keep anything the user explicitly hid
    if (order.includes(id)) return false;
    
    // Only keep truly non-configurable columns
    return true;
  });

  return [...ordered, ...genericColumns, ...extras];
}

export function SalesInvoiceListPage() {
  const navigate = useNavigate();
  const [inlineDetailDocName, setInlineDetailDocName] = useState<string | null>(null);
  const handleInlineClose = useCallback(() => setInlineDetailDocName(null), []);
  const { detailPanelItem, panelItem, openDetailPanel, closeDetailPanel } = useDetailPanelState();

  const renderSubRow = useCallback(
    (row: SalesInvoice, columnInfo: SubRowColumnInfo): ReactNode | null => {
      if (!row.name || row.name !== inlineDetailDocName) return null;
      return (
        <GenericInlineDetailPanel
          key={row.name}
          doctype="Sales Invoice"
          docName={row.name}
          rowData={row as unknown as Record<string, unknown>}
          onClose={handleInlineClose}
          columns={columnInfo}
        />
      );
    },
    [inlineDetailDocName, handleInlineClose],
  );

  const buildColumns = useMemo(
    () => ({
      items: _items,
      visibleFieldOrder,
      availableFields
    }: {
      items: SalesInvoice[];
      visibleFieldOrder?: string[];
      availableFields?: Array<{ fieldname: string; label: string; fieldtype?: string }>;
    }) => {
      const baseCols = createSalesInvoiceColumns({ visibleFieldOrder, availableFields });
      return injectActionsColumn(baseCols, {
        onToggleDetail: (name) => setInlineDetailDocName((prev) => prev === name ? null : name),
        onOpen: (name) => openDetailPanel(name),
      });
    },
    [navigate]
  );
  
  // Note: For v1, we're not passing columnIds to enable full meta-driven behavior
  // This allows all safe meta fields to appear in List Settings, not just those with existing columns
  // In the future, we can add strictMode with columnIds to restrict to only fields with bespoke columns

  return (
    <div className={LIST_PAGE_SHELL}>
      <div>
        <DocTypeListPageShell<SalesInvoice>
          doctype="Sales Invoice"
          columns={buildColumns}
          filterStoreKey="selling.sales_invoice"
          useListHook={useSalesInvoiceListWithPagination}
          defaultSorting={[{ id: "posting_date", desc: true }]}
          searchPlaceholder="Search by invoice number or customer..."
          title="Sales Invoices"
          newButtonLabel="New Invoice"
          onNewClick={() => navigate(buildNewRoute("Sales Invoice"))}
          onRowClick={undefined}
          renderSubRow={renderSubRow}
          activeSubRowId={inlineDetailDocName ?? undefined}
          getRowId={(row) => row.name}
          inlineEditable
          isInlineEditMode
          compactMode
          noPadding
          fullWidth
          className="pt-0"
          tableContainerClassName="overflow-x-auto overflow-y-hidden border-y border-border bg-white dark:bg-neutral-950"
          breadcrumbs={[
            {
              label: "Sales Invoices",
              icon: (
                <span className="flex items-center justify-center h-5 w-5 rounded-xs bg-indigo-500 shrink-0">
                  <FileText className="h-3 w-3 text-white" />
                </span>
              ),
            },
          ]}
          enableViewModeSwitch={true}
          listSettingsConfig={{
            key: "selling.sales_invoice",
            meta: {
              doctype: "Sales Invoice",
              includeFieldtypes: [
                "Data",
                "Currency",
                "Date",
                "Datetime",
                "Check",
                "Int",
                "Float",
                "Link",
                "Select",
                "Small Text",
                "Text",
                "Long Text",
              ],
              excludeFieldtypes: [
                "Section Break",
                "Column Break",
                "HTML",
                "Table",
                "Table MultiSelect",
                "Button",
                "Heading",
              ],
              excludeFieldnames: [
                "naming_series",
                "owner",
                "creation",
                "modified",
                "modified_by",
                "idx",
                "__islocal",
                "__unsaved",
                "docstatus",
                "amended_from",
                "amended_date",
                "_assign",
                "_comment_count",
                "_liked_by",
              ],
              respectInListView: true,
              requiredFieldnames: ["name", "customer"],
              strictMode: false,
            },
          }}
          systemColumnsConfig={{
            enabled: true,
            showAssignedTo: true,
            showCreated: true,
            showComments: true,
            showLikes: true,
            assignedToField: "_assign",
            commentCountField: "comment_count",
            likedByField: "_liked_by",
            createdField: "creation",
          }}
        />
      </div>

      <GenericDetailPanel
        open={!!detailPanelItem}
        onClose={closeDetailPanel}
        doctype="Sales Invoice"
        docName={panelItem?.name ?? ""}
        docLabel={panelItem?.label}
      />
    </div>
  );
}

